"use client";

import React from "react";
import { Wrench, ShieldCheck, Clock, Flame, PhoneCall, MapPin, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
const keyBenefits = [{
  id: 1,
  title: "Authorized Rheem Specialists",
  desc: "Factory-trained technicians for gas, electric, and tankless units."
}, {
  id: 2,
  title: "24/7 Rapid Emergency Response",
  desc: "Immediate dispatch across Jonesboro and surrounding areas."
}, {
  id: 3,
  title: "Original Parts & Full Warranty",
  desc: "Genuine Rheem replacement parts with guaranteed workmanship."
}];
const stats = [{
  label: "Emergency Service",
  value: "24/7 Available"
}, {
  label: "Response Time",
  value: "Under 60 Min"
}, {
  label: "Rheem Models",
  value: "All Tank & Tankless"
}];
export default function RheemHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 18
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="rheemhero" data-nav="light" className="relative overflow-hidden bg-background py-16 lg:py-24 border-b border-border">
      {/* Decorative backdrop elements */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 right-0 size-96 rounded-full bg-accent/15 blur-3xl" />
        <div className="absolute top-1/2 left-0 size-80 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--color-muted)_1px,transparent_1px)] [background-size:24px_24px] opacity-40" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16" variants={containerVariants} initial="hidden" animate="visible">
          {/* Main Hero Content */}
          <div className="flex flex-col items-start lg:col-span-7">
            <motion.div variants={itemVariants} className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                <Flame className="size-3.5 text-accent mr-1" />
                Rheem Water Heater Specialists
              </Badge>
              <Badge variant="outline" className="border-border px-3 py-1 text-xs font-medium text-muted-foreground">
                Jonesboro, GA & Surrounding Areas
              </Badge>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-5xl leading-tight sm:leading-tight lg:leading-tight">
              Expert Rheem Water Heater Repair & Installation
            </motion.h1>

            <motion.p variants={itemVariants} className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Fast, reliable service for Rheem tank, tankless, hybrid, gas, and electric systems. From emergency leaking tanks to complete replacements, our licensed Jonesboro plumbing crew is ready 24/7.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={itemVariants} className="mt-8 flex w-full flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild size="lg" className="shadow-md text-base font-semibold">
                <Link to="/contact/">
                  <PhoneCall className="size-4 mr-2" />
                  Request Emergency Service
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border text-base font-medium">
                <Link to="/service-areas/">
                  <MapPin className="size-4 mr-2 text-primary" />
                  View Service Areas
                </Link>
              </Button>
            </motion.div>

            {/* Quick feature list */}
            <motion.div variants={itemVariants} className="mt-10 grid w-full grid-cols-1 gap-3 sm:grid-cols-3 border-t border-border/80 pt-6">
              {stats.map((item, index) => <div key={item.label} data-index={index} className="flex flex-col">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {item.label}
                  </span>
                  <span className="text-base font-bold text-foreground mt-0.5">
                    {item.value}
                  </span>
                </div>)}
            </motion.div>
          </div>

          {/* Hero Visual Card / Brand Identity */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <div className="relative mx-auto w-full max-w-md lg:max-w-none">
              {/* Primary Visual Card */}
              <Card className="border-border bg-card shadow-xl overflow-hidden">
                <div className="relative aspect-4/3 w-full overflow-hidden bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/rheem_water_heater_technician.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/rheem_water_heater_technician.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/rheem_water_heater_technician.png-wvc-sizes" alt="Plumber servicing a Rheem water heater system" loading="lazy" decoding="async" className="size-full object-cover object-center" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-transparent to-transparent" />
                  
                  {/* Floating badge inside image */}
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-background">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="size-5 text-accent" />
                      <span className="text-sm font-semibold tracking-wide">Licensed & Insured Plumbing</span>
                    </div>
                    <span className="rounded-full bg-primary/90 px-2.5 py-0.5 text-xs font-semibold text-primary-foreground backdrop-blur-sm">
                      24/7
                    </span>
                  </div>
                </div>

                <CardContent className="p-6">
                  <div className="flex items-center justify-between border-b border-border pb-4 mb-4">
                    <div>
                      <h3 className="font-default text-lg font-bold text-card-foreground">
                        Rheem Certified Support
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Installation, Diagnostics & Part Replacement
                      </p>
                    </div>
                    <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Wrench className="size-5" />
                    </div>
                  </div>

                  <div className="space-y-3">
                    {keyBenefits.map((benefit, bIndex) => <div key={benefit.id} data-index={bIndex} className="flex items-start gap-3">
                        <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-accent/20 text-accent-foreground">
                          <CheckCircle2 className="size-3.5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-card-foreground">
                            {benefit.title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {benefit.desc}
                          </p>
                        </div>
                      </div>)}
                  </div>

                  <div className="mt-6 flex flex-col gap-2 pt-2 border-t border-border">
                    <Button asChild variant="secondary" className="w-full justify-between">
                      <Link to="/water-heater-repair/">
                        <span>Explore Rheem Repair Solutions</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Decorative Accent Tag */}
              <div className="absolute -bottom-4 -left-4 hidden sm:flex items-center gap-2.5 rounded-lg border border-border bg-popover px-4 py-2.5 shadow-lg">
                <Clock className="size-4 text-accent" />
                <span className="text-xs font-medium text-popover-foreground">
                  Immediate 24-Hour Dispatch Available
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}